import React from 'react';
import { Formik, Form, useField } from 'formik';
import * as Yup from 'yup';
import Button from '../../Button/Button';
import { usePostFactSheetMutation } from '../../../services/plaintiffSheets/plaintiffSheetsEndpoint';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../components/Toast/ToastContext';

interface IFactSheetProps {
  closePopUp: () => void;
}

const FactSheetInput: React.FC = () => {
  const [field, meta, helpers] = useField('factSheet');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    field.onChange(e); // updates the value
    if (meta.error) {
      helpers.setError(undefined); // clears the error
    }
  };

  return (
    <div className="mb-4">
      <label htmlFor="factSheet" className="text-sm font-medium text-gray-700">
        Fact Sheet Name <span className="text-red-500">*</span>
      </label>
      <input
        {...field}
        id="factSheet"
        type="text"
        onChange={handleChange}
        placeholder="Please enter relevant fact sheet name"
        className={`w-full text-sm text-ds-gray-500 rounded-md p-2 mt-2 outline-none placeholder:text-gray-400 placeholder:italic placeholder:text-sm border ${
          meta.error ? 'border-ds-red-400 border-2' : 'border-ds-gray-100'
        }`}
      />
      {meta.error && <div className="text-ds-red-400 text-sm mt-1">{meta.error}</div>}
    </div>
  );
};

const FactSheet: React.FC<IFactSheetProps> = ({ closePopUp }) => {
  const { addToast } = useToast();
  const [postFactSheet, { isLoading }] = usePostFactSheetMutation();

  const navigate = useNavigate();

  const handleSubmit = async (
    values: { factSheet: string },
    { setFieldError }: { setFieldError: (field: string, message: string) => void }
  ) => {
    try {
      const factData = {
        name: values.factSheet.trim(),
      };
      const response = await postFactSheet({ data: factData }).unwrap();

      if (response?.data) {
        addToast('Fact Sheet Added', 'Fact Sheet is added successfully.', 'success');
        navigate(`/plaintiffSheets/section/${response.data._id}`);
      }
    } catch (err: any) {
      if (err?.data?.message === 'Fact sheet name already exists') {
        setFieldError('factSheet', err.data.message);
      } else {
        console.error('Submission Error:', err);
        addToast('Error', 'Something went wrong. Please try again.', 'error');
      }
    }
  };

  return (
    <Formik
      initialValues={{ factSheet: '' }}
      validationSchema={Yup.object().shape({
        factSheet: Yup.string().required('Please provide a relevant fact sheet name.'),
      })}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={(values, { setFieldError }) => handleSubmit(values, { setFieldError })}
    >
      <Form>
        <FactSheetInput />

        <div className="flex justify-end h-10 gap-2  space-x-2">
          <Button size="sm" color="outlined" onClick={closePopUp} type="button">
            Cancel
          </Button>
          <Button size="sm" color="primary" type="submit" loading={isLoading}>
            Start
          </Button>
        </div>
      </Form>
    </Formik>
  );
};

export default FactSheet;
